import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { useContext, useEffect, useState } from 'react';
import { useStore } from 'zustand';
import { ConsentFooter } from '@/idv2/components/consent-footer';
import { NAVIGATION_HEADER_PORTAL_ID } from '@/idv2/components/constants';
import { InitShimmer } from '@/idv2/components/init-shimmer';
import { LoadingFallback } from '@/idv2/components/loading-fallback';
import { type IDV2Store, StoreContext } from '@/idv2/features/idv-to-idv2/idv2-provider';
import { useFootprint } from '@/idv2/hooks/use-footprint';
import { getLogger } from '@/idv2/utils/logger';
import { NavigationOverrideProvider, useNavigationOverride } from './hooks/use-navigation-override';
import { useRequiredConsent } from './hooks/use-required-consent';
import { NavigationHeader } from './navigation-header';
type AppShellProps = {
  children?: ReactNode;
  showCloseButton?: boolean;
  isErrorScreen?: boolean;
};
const {
  logInfo
} = getLogger({
  name: 'idv2',
  location: 'app-shell'
});
const AppShellContent = t0 => {
  const $ = _c(27);
  const {
    children,
    showCloseButton: t1,
    isErrorScreen: t2
  } = t0;
  const showCloseButton = t1 === undefined ? false : t1;
  const isErrorScreen = t2 === undefined ? false : t2;
  const {
    navigation,
    isLoading,
    data: t3
  } = useFootprint();
  const {
    onboarding
  } = t3;
  const [hasRenderedScreen, setHasRenderedScreen] = useState(false);
  let t4;
  let t5;
  if ($[0] !== isLoading) {
    t4 = () => {
      if (!isLoading) {
        setHasRenderedScreen(true);
      }
    };
    t5 = [isLoading];
    $[0] = isLoading;
    $[1] = t4;
    $[2] = t5;
  } else {
    t4 = $[1];
    t5 = $[2];
  }
  useEffect(t4, t5);
  const {
    isNavigationOverridden
  } = useNavigationOverride();
  const requiredConsent = useRequiredConsent();
  const store = useContext(StoreContext);
  const hideNavigationButtons = useStore(store, _temp);
  const isTransfer = useStore(store, _temp2);
  const canGoBack = showCloseButton || isTransfer ? false : onboarding.canGoBack;
  let t6;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = document.getElementById(NAVIGATION_HEADER_PORTAL_ID);
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const needsPortalTarget = !t6;
  let t7;
  if ($[4] !== navigation || $[5] !== showCloseButton) {
    t7 = () => {
      logInfo("User clicked close button", {
        showCloseButton
      });
      navigation.close?.();
    };
    $[4] = navigation;
    $[5] = showCloseButton;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const handleClose = t7;
  let t8;
  if ($[7] !== navigation || $[8] !== showCloseButton) {
    t8 = () => {
      logInfo("User clicked back button", {
        showCloseButton
      });
      navigation.back?.();
    };
    $[7] = navigation;
    $[8] = showCloseButton;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const handleBack = t8;
  const shouldRenderNavigationHeader = !isNavigationOverridden && !isLoading;
  const hasNavigationButtons = !hideNavigationButtons && (canGoBack || navigation.close);
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = needsPortalTarget && <header id={NAVIGATION_HEADER_PORTAL_ID} className="sticky top-0 z-10 px-6 pt-4 min-h-14" style={{
      background: "var(--bifrost-container-bg, var(--background-color-primary))"
    }} />;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== canGoBack || $[12] !== handleBack || $[13] !== handleClose || $[14] !== hasNavigationButtons || $[15] !== isNavigationOverridden || $[16] !== shouldRenderNavigationHeader) {
    t10 = shouldRenderNavigationHeader ? <NavigationHeader canGoBack={canGoBack ?? false} onBack={handleBack} onClose={hasNavigationButtons ? handleClose : undefined} hideButtons={!hasNavigationButtons} /> : !isNavigationOverridden && <div className="h-14 shrink-0" />;
    $[11] = canGoBack;
    $[12] = handleBack;
    $[13] = handleClose;
    $[14] = hasNavigationButtons;
    $[15] = isNavigationOverridden;
    $[16] = shouldRenderNavigationHeader;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== children || $[19] !== hasRenderedScreen || $[20] !== isErrorScreen || $[21] !== isLoading || $[22] !== requiredConsent) {
    t11 = isLoading ? hasRenderedScreen ? <LoadingFallback /> : <InitShimmer reserveHeader={false} /> : <>{children}<ConsentFooter requiredConsent={isErrorScreen ? [] : requiredConsent} /></>;
    $[18] = children;
    $[19] = hasRenderedScreen;
    $[20] = isErrorScreen;
    $[21] = isLoading;
    $[22] = requiredConsent;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t10 || $[25] !== t11) {
    t12 = <>{t9}{t10}{t11}</>;
    $[24] = t10;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  return t12;
};
export const AppShell = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <NavigationOverrideProvider><AppShellContent {...props} /></NavigationOverrideProvider>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
function _temp(state) {
  return state.hideNavigationButtons;
}
function _temp2(state_0) {
  return state_0.initialValues.isTransfer;
}